/* RESPONSIBLE TEAM: team-standalone */
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type Platform from './platform';
import type IntercomMessengerConfiguration from 'embercom/models/standalone/intercom-messenger-configuration';
import { IntegrationState, IntegrationType, StandalonePlatform } from '../constants';
import type ApplicationInstance from '@ember/application/instance';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { SalesforceConversationExtractionRow } from './component-data/conversation-extraction-row-data';
import SalesforceKnowledgeHubModalData from './component-data/salesforce/knowledge-hub-modal';
import SalesforceConversationExcerpt from './component-data/salesforce/conversation-excerpt';
import SalesforceOnboardingGuideStep from './component-data/salesforce/onboarding-guide-step';
import SalesforceRequestAccessData from './component-data/salesforce/request-access';
import SalesforceConversationIngestion from './component-data/salesforce/conversation-ingestion';
import type Store from '@ember-data/store';
import type SalesforceLiveChatConfiguration from 'embercom/models/standalone/salesforce-live-chat-configuration';
import type SalesforceInAppMessagingConfiguration from 'embercom/models/standalone/salesforce-in-app-messaging-configuration';
import { tracked } from '@glimmer/tracking';
import SalesforceDataLoader from 'embercom/services/fin-standalone-service/salesforce-data-loader';
import { taskFor } from 'ember-concurrency-ts';
import type {
  SalesforceSetupData,
  SalesforceImportOnlyConfig,
} from 'embercom/objects/standalone/salesforce';
import SalesforceChannelTargetingBodyData from './component-data/salesforce/channel-targeting-body-data';
import { SalesforceAiContentLibraryData } from './component-data/ai-content-library';
import { SalesforceKnowledgeContentData } from './component-data/knowlege-content-data';
import { SalesforceComposerOverflowMenuTooltipData } from './component-data/overflow-menu-tooltip-data';
import { SalesforceExampleCodeSnippetData } from './component-data/example-code-snippet-data';
import StandardizedPlatform from 'embercom/objects/standalone/platforms/standardized-platform';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';

export default class SalesforcePlatform extends StandardizedPlatform<SalesforceConfiguration> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;

  salesforceDataLoader: SalesforceDataLoader;
  @tracked intercomMessengerConfig?: IntercomMessengerConfiguration;
  @tracked salesforceImportOnlyConfig?: SalesforceImportOnlyConfig;

  // Store config data per config ID
  @tracked configurationData = new Map<
    string,
    {
      setupData: SalesforceSetupData;
      inAppMessagingConfig?: SalesforceInAppMessagingConfiguration;
      liveChatConfig?: SalesforceLiveChatConfiguration;
      selectedCaseOrigins: Array<string>;
    }
  >();

  @tracked hasRequiredSalesforceFinPermissions = false;
  @tracked hasPermissionPackageBeenInstalled = false;

  constructor(
    owner: ApplicationInstance,
    inputs: {
      salesforceConfig?: SalesforceConfiguration;
      intercomMessengerConfig?: IntercomMessengerConfiguration;
    },
  ) {
    super(owner);
    this.salesforceConfig = inputs.salesforceConfig;
    this.intercomMessengerConfig = inputs.intercomMessengerConfig;
    this.salesforceDataLoader = new SalesforceDataLoader(this);
  }

  createDefaultConfigData() {
    return {
      setupData: {
        admins: [],
        queues: [],
        caseOrigins: [],
        supportEmails: [],
        hasPermissionSet: undefined,
        hasPermissionSetAssignedToInstaller: undefined,
        hasDigitalExperiences: undefined,
        permissionsHealthcheck: [],
        copilot: {
          isInstalled: false,
          hasCustomSettingsFields: false,
          installedOnLightningPages: false,
          unableToDetectLightningPageInstallation: false,
          lightningWebSecurityEnabled: false,
        },
      },
      selectedCaseOrigins: [],
    };
  }

  // Helper to update configuration data and trigger reactivity
  setConfigurationData(
    configId: string,
    data: {
      setupData?: SalesforceSetupData;
      inAppMessagingConfig?: SalesforceInAppMessagingConfiguration;
      liveChatConfig?: SalesforceLiveChatConfiguration;
      selectedCaseOrigins?: Array<string>;
    },
  ) {
    let existing = this.configurationData.get(configId) || this.createDefaultConfigData();
    this.configurationData.set(configId, { ...existing, ...data });
    // Trigger reactivity by reassigning the Map
    this.configurationData = new Map(this.configurationData);
  }

  // Backwards-compatible getters and setters that proxy to active configuration
  get salesforceSetupData(): SalesforceSetupData {
    let activeId = this.salesforceConfig?.id;
    if (!activeId) {
      return this.createDefaultConfigData().setupData;
    }
    return (
      this.configurationData.get(activeId)?.setupData ?? this.createDefaultConfigData().setupData
    );
  }

  set salesforceSetupData(value: SalesforceSetupData) {
    let activeId = this.salesforceConfig?.id;
    if (activeId) {
      this.setConfigurationData(activeId, { setupData: value });
    }
  }

  get salesforceInAppMessagingConfig(): SalesforceInAppMessagingConfiguration | undefined {
    let activeId = this.salesforceConfig?.id;
    if (!activeId) {
      return undefined;
    }
    return this.configurationData.get(activeId)?.inAppMessagingConfig;
  }

  set salesforceInAppMessagingConfig(value: SalesforceInAppMessagingConfiguration | undefined) {
    let activeId = this.salesforceConfig?.id;
    if (activeId && value) {
      this.setConfigurationData(activeId, { inAppMessagingConfig: value });
    }
  }

  get allSalesforceInAppMessagingConfigs(): SalesforceInAppMessagingConfiguration[] {
    return Array.from(this.configurationData.values())
      .map((data) => data.inAppMessagingConfig)
      .filter((config) => config !== undefined);
  }

  get hasAnySalesforceInAppMessagingConnected(): boolean {
    return this.allSalesforceInAppMessagingConfigs.some((config) => config.isConnected);
  }

  get salesforceLiveChatConfig(): SalesforceLiveChatConfiguration | undefined {
    let activeId = this.salesforceConfig?.id;
    if (!activeId) {
      return undefined;
    }
    return this.configurationData.get(activeId)?.liveChatConfig;
  }

  set salesforceLiveChatConfig(value: SalesforceLiveChatConfiguration | undefined) {
    let activeId = this.salesforceConfig?.id;
    if (activeId && value) {
      this.setConfigurationData(activeId, { liveChatConfig: value });
    }
  }

  get selectedSalesforceCaseOrigins(): Array<string> {
    let activeId = this.salesforceConfig?.id;
    if (!activeId) {
      return [];
    }
    return this.configurationData.get(activeId)?.selectedCaseOrigins ?? [];
  }

  set selectedSalesforceCaseOrigins(value: Array<string>) {
    let activeId = this.salesforceConfig?.id;
    if (activeId) {
      this.setConfigurationData(activeId, { selectedCaseOrigins: value });
    }
  }

  get salesforceConfig() {
    return this.activeConfiguration;
  }

  set salesforceConfig(value: SalesforceConfiguration | undefined) {
    this.activeConfiguration = value;
  }

  get salesforceConfigs() {
    return this.configurations;
  }

  set salesforceConfigs(value: Array<SalesforceConfiguration> | undefined) {
    this.configurations = value;
  }

  get componentData(): Platform['componentData'] {
    return {
      'conversation-extraction': new SalesforceConversationExtractionRow(this),
      'knowledge-hub-modal': new SalesforceKnowledgeHubModalData(this),
      'conversation-excerpt': new SalesforceConversationExcerpt(this),
      'onboarding-guide-step': new SalesforceOnboardingGuideStep(this),
      'request-access': new SalesforceRequestAccessData(this),
      'channel-targeting-body': new SalesforceChannelTargetingBodyData(),
      'ai-content-library': SalesforceAiContentLibraryData,
      'knowledge-content': SalesforceKnowledgeContentData,
      'composer-overflow-menu-tooltip': SalesforceComposerOverflowMenuTooltipData,
      'example-code-snippet': SalesforceExampleCodeSnippetData,
      'conversation-ingestion': new SalesforceConversationIngestion(this),
    } as const;
  }

  async loadConfigData() {
    if (taskFor(this.salesforceDataLoader.loadSalesforceConfig).isRunning) {
      return taskFor(this.salesforceDataLoader.loadSalesforceConfig).lastRunning!;
    } else {
      return taskFor(this.salesforceDataLoader.loadSalesforceConfig).perform();
    }
  }

  async loadRequiredData() {
    await this.salesforceDataLoader.performLoads();
    this.salesforceDataLoader.performLazyLoads();
    await this.loadMessengerConfig();
  }

  async loadDataFields() {
    await super.loadDataFields();
  }

  async loadMessengerConfig() {
    this.intercomMessengerConfig = await this.store.findRecord(
      'standalone/intercom-messenger-configuration',
      this.finStandaloneService.app.id,
    );
  }

  get integrationTypes() {
    return [IntegrationType.SalesforceCases];
  }

  get isConnected() {
    return this.salesforceConfig?.isConnected || false;
  }

  get hasInstalledCopilot() {
    return this.salesforceSetupData.copilot.isInstalled;
  }

  get supportsHistoricalSyncing() {
    return true;
  }

  get historicalSyncingEnabled() {
    // TODO: SA Generic platform migration – This isConversationSyncingEnabled is kind of circular, move it to to the base object?
    return (this.isConnected && this.finStandaloneService.isConversationSyncingEnabled) || false;
  }

  get finIsLive() {
    return (
      this.salesforceConfig?.isEnabled || this.intercomMessengerConfig?.isMessengerEnabled || false
    );
  }

  get finIsPaused() {
    return (
      this.salesforceConfig?.isPaused || this.intercomMessengerConfig?.isMessengerPaused || false
    );
  }

  get connectionState() {
    return this.calculateHighestIntegrationState([
      this.salesforceConfig?.status ?? IntegrationState.NotConfigured,
      this.intercomMessengerConfig?.integrationState ?? IntegrationState.NotConfigured,
    ]);
  }

  get icon() {
    return 'salesforce' as const;
  }

  get platformKey() {
    return StandalonePlatform.Salesforce;
  }

  override get canUseReactStepBasedIA(): boolean {
    return true;
  }

  get hasMessengerSettings() {
    return this.finStandaloneService.app.canUseFeature(
      'team-standalone-salesforce-user-verification-for-miaw',
    );
  }

  get supportsFinPlayground() {
    return true;
  }

  get supportsConversationSyncing() {
    return true;
  }

  get supportsErrorLogs() {
    return true;
  }

  get showOauthBanner() {
    return false;
  }

  findMappedConversationField(attributeIdentifier: string | undefined) {
    return this.ticketFields.find(
      (externalFieldIdentifier: ExternalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  findMappedUserField(attributeIdentifier: string | undefined) {
    return this.userFields.find(
      (externalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  findMappedCompanyField(attributeIdentifier: string | undefined) {
    return this.companyFields.find(
      (externalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  isMappedUserAttribute(attributeIdentifier: string) {
    return super.isMappedUserAttribute(attributeIdentifier);
  }

  isMappedCompanyAttribute(attributeIdentifier: string) {
    return super.isMappedCompanyAttribute(attributeIdentifier);
  }

  isMappedConversationAttribute(attributeIdentifier: string) {
    return super.isMappedConversationAttribute(attributeIdentifier);
  }

  get ticketFields() {
    return super.ticketFields;
  }

  get userFields() {
    return super.userFields;
  }

  get companyFields() {
    return super.companyFields;
  }
}
